import {
  Stack,
  Group,
  Table,
  Text,
  Button,
  Badge,
  ActionIcon,
  Tooltip,
  Modal,
  NumberInput,
  Select,
  Switch,
  Loader,
  Center,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { useDisclosure } from "@mantine/hooks";
import { IconPlus, IconEdit, IconTrash } from "@tabler/icons-react";
import { useState } from "react";
import {
  StainProviderCapability,
  Stain,
} from "../../../services/admin/stains.service";
import {
  useStainProviderCapabilities,
  useActiveStainProviders,
  useAddStainProviderCapability,
  useUpdateStainProviderCapability,
  useDeleteStainProviderCapability,
} from "../../../hooks/admin/useStains";

interface ProviderCapabilitiesPanelProps {
  stain: Stain;
}

interface CapabilityFormValues {
  providerId: string;
  cost: number | "";
  turnaroundDays: number | "";
  isPreferred: boolean;
}

export default function ProviderCapabilitiesPanel({
  stain,
}: ProviderCapabilitiesPanelProps) {
  const [modalOpened, { open: openModal, close: closeModal }] =
    useDisclosure(false);
  const [editingCapability, setEditingCapability] =
    useState<StainProviderCapability | null>(null);

  const { data: capabilities = [], isLoading } = useStainProviderCapabilities(
    stain.id
  );
  const { data: providers = [] } = useActiveStainProviders();
  const addCapability = useAddStainProviderCapability();
  const updateCapability = useUpdateStainProviderCapability();
  const deleteCapability = useDeleteStainProviderCapability();

  const form = useForm<CapabilityFormValues>({
    initialValues: {
      providerId: "",
      cost: "",
      turnaroundDays: "",
      isPreferred: false,
    },
    validate: {
      providerId: (value) => (!value ? "Provider is required" : null),
    },
  });

  // Get providers not already linked to this stain
  const availableProviders = providers.filter(
    (p) =>
      !capabilities.some((c) => c.stainProviderId === p.id) ||
      editingCapability?.stainProviderId === p.id
  );

  const handleAdd = () => {
    setEditingCapability(null);
    form.reset();
    openModal();
  };

  const handleEdit = (capability: StainProviderCapability) => {
    setEditingCapability(capability);
    form.setValues({
      providerId: capability.stainProviderId,
      cost: capability.cost ?? "",
      turnaroundDays: capability.turnaroundDays ?? "",
      isPreferred: capability.isPreferred,
    });
    openModal();
  };

  const handleDelete = async (capability: StainProviderCapability) => {
    if (
      window.confirm(
        `Remove ${capability.stainProvider?.name || "this provider"} from ${
          stain.code
        }?`
      )
    ) {
      await deleteCapability.mutateAsync({
        capabilityId: capability.id,
        stainId: stain.id,
      });
    }
  };

  const handleSubmit = async (values: CapabilityFormValues) => {
    if (editingCapability) {
      await updateCapability.mutateAsync({
        capabilityId: editingCapability.id,
        stainId: stain.id,
        cost: values.cost === "" ? undefined : values.cost,
        turnaroundDays:
          values.turnaroundDays === "" ? undefined : values.turnaroundDays,
        isPreferred: values.isPreferred,
      });
    } else {
      await addCapability.mutateAsync({
        stainId: stain.id,
        providerId: values.providerId,
        cost: values.cost === "" ? undefined : values.cost,
        turnaroundDays:
          values.turnaroundDays === "" ? undefined : values.turnaroundDays,
        isPreferred: values.isPreferred,
      });
    }
    closeModal();
    form.reset();
  };

  if (isLoading) {
    return (
      <Center p="md">
        <Loader size="sm" />
      </Center>
    );
  }

  return (
    <Stack gap="sm" p="md" bg="gray.0" style={{ borderRadius: 8 }}>
      <Group justify="space-between">
        <Text fw={500} size="sm">
          Provider Capabilities
        </Text>
        <Button
          size="xs"
          leftSection={<IconPlus size={14} />}
          variant="light"
          onClick={handleAdd}
          disabled={availableProviders.length === 0 && !editingCapability}
        >
          Add Provider
        </Button>
      </Group>

      {capabilities.length === 0 ? (
        <Text size="sm" c="dimmed" ta="center" py="sm">
          No external providers configured for this stain
        </Text>
      ) : (
        <Table striped>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Provider</Table.Th>
              <Table.Th>Cost</Table.Th>
              <Table.Th>Turnaround</Table.Th>
              <Table.Th style={{ width: 100 }}>Actions</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {capabilities.map((cap) => (
              <Table.Tr key={cap.id}>
                <Table.Td>
                  <Group gap="xs">
                    <Text size="sm">
                      {cap.stainProvider?.name || "Unknown"}
                    </Text>
                    {cap.isPreferred && (
                      <Badge size="xs" color="green" variant="light">
                        Preferred
                      </Badge>
                    )}
                    {stain.defaultProviderId === cap.stainProviderId && (
                      <Badge size="xs" color="blue" variant="light">
                        Default
                      </Badge>
                    )}
                  </Group>
                </Table.Td>
                <Table.Td>
                  {cap.cost != null ? `$${Number(cap.cost).toFixed(2)}` : "—"}
                </Table.Td>
                <Table.Td>
                  {cap.turnaroundDays != null
                    ? `${cap.turnaroundDays} days`
                    : "—"}
                </Table.Td>
                <Table.Td>
                  <Group gap="xs">
                    <Tooltip label="Edit">
                      <ActionIcon
                        variant="subtle"
                        size="sm"
                        onClick={() => handleEdit(cap)}
                      >
                        <IconEdit size={14} />
                      </ActionIcon>
                    </Tooltip>
                    <Tooltip label="Remove">
                      <ActionIcon
                        variant="subtle"
                        color="red"
                        size="sm"
                        onClick={() => handleDelete(cap)}
                        loading={deleteCapability.isPending}
                      >
                        <IconTrash size={14} />
                      </ActionIcon>
                    </Tooltip>
                  </Group>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}

      {/* Add/Edit Modal */}
      <Modal
        opened={modalOpened}
        onClose={closeModal}
        title={editingCapability ? "Edit Provider Capability" : "Add Provider"}
        size="sm"
        closeOnClickOutside={false}
      >
        <form noValidate onSubmit={form.onSubmit(handleSubmit)}>
          <Stack gap="md">
            <Select
              label="Provider"
              placeholder="Select provider"
              data={availableProviders.map((p) => ({
                value: p.id,
                label: p.name,
              }))}
              required
              disabled={!!editingCapability}
              {...form.getInputProps("providerId")}
            />

            <NumberInput
              label="Cost"
              placeholder="Enter cost"
              prefix="$"
              decimalScale={2}
              min={0}
              {...form.getInputProps("cost")}
            />

            <NumberInput
              label="Turnaround Days"
              placeholder="Enter turnaround time"
              suffix=" days"
              min={0}
              {...form.getInputProps("turnaroundDays")}
            />

            <Switch
              label="Preferred Provider"
              description="Mark as preferred for this stain"
              {...form.getInputProps("isPreferred", { type: "checkbox" })}
            />

            <Group justify="flex-end" mt="md">
              <Button variant="default" onClick={closeModal}>
                Cancel
              </Button>
              <Button
                type="submit"
                loading={addCapability.isPending || updateCapability.isPending}
              >
                {editingCapability ? "Update" : "Add"}
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>
    </Stack>
  );
}
